import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppUtil from "utilities/AppUtil";
import httpRequest from "../../services/httpRequest";
import snakecaseKeys from "snakecase-keys";

// Main URL Endpoint
const _urlPrefix = "/investigations";

export const fetchPageInvestigation = createAsyncThunk(
  "investigation/fetchPageInvestigation",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchInvestigationDetail = createAsyncThunk(
  "investigation/fetchInvestigationDetail",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const createInvestigation = createAsyncThunk(
  "investigation/createInvestigation",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.create.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateInvestigationStatus = createAsyncThunk(
  "investigation/updateInvestigationStatus",
  async (params) => {
    const { id, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/status`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateInvestigationStepStatus = createAsyncThunk(
  "investigation/updateInvestigationStepStatus",
  async (params) => {
    const { id, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/step-status`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchInvestigationEvidence = createAsyncThunk(
  "investigation/fetchInvestigationEvidence",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const uploadInvestigationEvidence = createAsyncThunk(
  "investigation/uploadInvestigationEvidence",
  async (params) => {
    const { id, files, description } = params;
    const data = new FormData();

    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        data.append("files", files[i]);
      }
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys({ description }))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.add.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateInvestigationEvidence = createAsyncThunk(
  "investigation/updateInvestigationEvidence",
  async (params) => {
    const { id, evidenceId, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const deleteInvestigationEvidence = createAsyncThunk(
  "investigation/deleteInvestigationEvidence",
  async (params) => {
    const { id, evidenceId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "DELETE",
      successMessage: "toast.delete.200",
    });
    if (!result) {
      return;
    }
    return { ...result.data, evidenceId };
  }
);

const initialState = {
  list: [],
  totalElements: 0,
  totalPages: 0,
  pageNumber: 1,
  detail: {},
  evidence: [],
};

const investigation = createSlice({
  name: "investigation",
  initialState: initialState,
  reducers: {},
  extraReducers: {
    [fetchPageInvestigation.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.list = payload.content || payload;
        state.totalElements = payload.totalElements || 0;
        state.totalPages = payload.totalPages || 0;
        state.pageNumber = payload.pageNumber || 1;
      }
    },
    [fetchInvestigationDetail.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [updateInvestigationStatus.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [updateInvestigationStepStatus.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [fetchInvestigationEvidence.fulfilled]: (state, { payload }) => {
      state.evidence = payload || [];
    },
    [uploadInvestigationEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.evidence.push(...(Array.isArray(payload) ? payload : [payload]));
      }
    },
    [updateInvestigationEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        const index = state.evidence.findIndex((e) => e.id === payload.id);
        if (index !== -1) {
          state.evidence[index] = payload;
        }
      }
    },
    [deleteInvestigationEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.evidence = state.evidence.filter((e) => e.id !== payload.evidenceId);
      }
    },
  },
});

const { reducer } = investigation;
export default reducer;

export const getInvestigationList = createSelector(
  (state) => state.investigation.list,
  (list) => list
);

export const getInvestigationPaging = createSelector(
  (state) => state.investigation,
  ({ totalElements, totalPages, pageNumber }) => ({ totalElements, totalPages, pageNumber })
);

export const getInvestigationDetail = createSelector(
  (state) => state.investigation.detail,
  (detail) => detail
);

export const getInvestigationEvidenceList = createSelector(
  (state) => state.investigation.evidence,
  (evidence) => evidence
);
